import React, { useEffect, useState } from 'react';
import { Alert, FormControl, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { ArrowBarLeft, ArrowBarRight, ExclamationTriangle } from 'react-bootstrap-icons';
import { toast } from 'react-toastify';
import useLocalization from '../../hooks/useLocalization';
import { reportService } from '../../services/reportServices';
import Editor from '../common/tiptap-editor/Editor';
import './index.scss';
import PlaceholderVariableItem from './PlaceholderVariableItem';
import PromptRegenerationModal from './PromptRegenerationModal';

const EditorWithPlaceholderSidebar = ({ data, onChange, documentVariables, id, setProgress, readOnly = false }) => {
  const { isRTL, translate } = useLocalization();
  const [variables, setVariables] = useState(documentVariables || []);
  const [selectedVariable, setSelectedVariable] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredVariables, setFilteredVariables] = useState(variables);
  const [reportData, setReportData] = useState(data);
  const [isSidebarOpen, setSidebarOpen] = useState(true);

  const handleReplacePlaceholder = async () => {
    setProgress(true);
    const requestObj = {
      aiResponse: selectedVariable.newAiResponse
    };

    const { response, error } = await reportService.replaceReportPlaceholder(id, requestObj);
    if (error) {
      toast.error(error);
      setProgress(false);
    } else {
      setIsModalOpen(false);
      setProgress(false);
      onChange({ body: response.data.body, aiResponse: response.data.aiResponse });
    }
  };

  const handleEditClick = variable => {
    const variableData = {
      ...variable,
      newPlaceholder: variable.placeholder
    };

    setSelectedVariable(variableData);
    setIsModalOpen(true);
  };

  const toggleSidebar = () => {
    setSidebarOpen(!isSidebarOpen);
  };

  const renderSidebarToggleButton = () => {
    return (
      <div
        className="position-absolute hover"
        style={{
          bottom: '10%',
          ...(isRTL ? { right: '16px' } : { left: '16px' }),
          transform: 'translateY(-50%)',
          transition: isRTL ? 'right 0.3s ease-in-out' : 'left 0.3s ease-in-out',
          zIndex: 999
        }}
      >
        <OverlayTrigger
          placement="top"
          overlay={<Tooltip>{isSidebarOpen ? translate('hide_sidebar') : translate('show_sidebar')}</Tooltip>}
        >
          <div
            className="bg-primary text-white rounded-circle shadow d-flex justify-content-center align-items-center"
            style={{ height: '45px', width: '45px' }}
            onClick={toggleSidebar}
          >
            {isSidebarOpen === isRTL ? (
              <ArrowBarRight size={20} className="hover" />
            ) : (
              <ArrowBarLeft size={20} className="hover" />
            )}
          </div>
        </OverlayTrigger>
      </div>
    );
  };

  const renderAlert = () => {
    return (
      <Alert
        variant="warning"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '10px',
          width: '100%',
          marginBottom: '10px',
          padding: '4px',
          fontSize: '14px'
        }}
      >
        <ExclamationTriangle size={14} /> {translate('uneditable_report')}
      </Alert>
    );
  };

  useEffect(() => {
    const query = searchQuery.toLowerCase();
    const filtered = variables.filter(
      variable =>
        variable.placeholder.toLowerCase().includes(query) ||
        JSON.stringify(variable?.value)
          .toLowerCase()
          .includes(query)
    );
    setFilteredVariables(filtered);
  }, [searchQuery, variables]);

  useEffect(() => {
    setReportData(data);
    setVariables(documentVariables);
  }, [data, documentVariables]);

  return (
    <div
      className="d-flex w-100 h-100 position-relative"
      style={{ direction: isRTL ? 'rtl' : 'ltr', overflow: 'hidden' }}
    >
      <div
        className="bg-white transition-width"
        style={{
          width: isSidebarOpen ? '25%' : '0',
          height: 'calc(100vh - 4rem)',
          transition: 'width 0.3s ease-in-out'
        }}
      >
        <div className="px-3 py-4 h-100">
          <h3 className="text-gray-300 mb-4 fw-normal" style={{ fontSize: '0.8rem' }}>
            {translate('document_variables')}
          </h3>
          <FormControl
            type="search"
            placeholder={translate('search_variables')}
            className="mb-3"
            size="sm"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            disabled={readOnly}
          />
          {filteredVariables.length > 0 ? (
            <div className="variable-list">
              {filteredVariables.map((variable, index) => (
                <PlaceholderVariableItem
                  key={index}
                  variable={variable}
                  index={index}
                  selectedVariable={selectedVariable}
                  handleEditClick={handleEditClick}
                  readOnly={readOnly}
                  translate={translate}
                />
              ))}
            </div>
          ) : (
            <div className="text-center">
              <p className="text-muted">{translate('no_variables_found')}</p>
            </div>
          )}
        </div>
      </div>
      <div
        style={{
          width: isSidebarOpen ? '75%' : '100%',
          transition: 'width 0.3s ease-in-out'
        }}
        className="d-flex flex-column justify-content-center h-100"
      >
        <div className="tiptap-container">
          {readOnly && renderAlert()}
          <Editor
            content={reportData}
            onContentChange={body => {
              onChange({ body });
            }}
            readOnly={readOnly}
          />
        </div>
      </div>
      {renderSidebarToggleButton()}
      <PromptRegenerationModal
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        selectedVariable={selectedVariable}
        setSelectedVariable={setSelectedVariable}
        isRTL={isRTL}
        readOnly={readOnly}
        handleReplacePlaceholder={handleReplacePlaceholder}
        id={id}
        translate={translate}
      />
    </div>
  );
};

export default EditorWithPlaceholderSidebar;
